import axios from "axios";

const isBrowser = typeof window !== "undefined";
const isLocalhost =
  isBrowser &&
  /^(localhost|127\.0\.0\.1|::1)$/.test(window.location.hostname);

const DEFAULT_PRODUCTION_API_URL =
  "https://track-trace-server-production.up.railway.app";

const getSafeApiBaseUrl = () => {
  if (!isBrowser) {
    return process.env.REACT_APP_API_URL || DEFAULT_PRODUCTION_API_URL;
  }

  const configuredApiUrl = String(process.env.REACT_APP_API_URL || "").trim();
  if (configuredApiUrl) {
    return configuredApiUrl;
  }

  if (isLocalhost) {
    return window.location.origin;
  }

  try {
    const parsed = new URL(configuredApiUrl);
    const isConfiguredLocalhost = /^(localhost|127\.0\.0\.1|::1)$/i.test(
      parsed.hostname
    );

    // Never allow a deployed HTTPS page to call a localhost backend.
    if (isConfiguredLocalhost) {
      return DEFAULT_PRODUCTION_API_URL;
    }

    // Avoid mixed-content calls from HTTPS pages to plain HTTP APIs.
    if (
      window.location.protocol === "https:" &&
      parsed.protocol === "http:"
    ) {
      return DEFAULT_PRODUCTION_API_URL;
    }

    return configuredApiUrl;
  } catch (error) {
    return DEFAULT_PRODUCTION_API_URL;
  }
};

// Centralized API URL.
// In local development, always stay on the local origin so CRA's proxy can route to the backend.
// In production, use the deployed API URL.
export const API_BASE_URL = getSafeApiBaseUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Still send cookies as fallback
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - adds Authorization header from localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors (token expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login if unauthorized
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      // Only redirect if not already on login page
      if (!error.config?.skipAuthRedirect &&
          !window.location.pathname.includes("/login") &&
          !window.location.pathname.includes("/auth")) {
        window.location.href = "/auth/login";
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions for token management
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("authToken", token);
  } else {
    localStorage.removeItem("authToken");
  }
};

export const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

export const clearAuth = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
};

// Helper for fetch calls with auth header (for files still using fetch)
export const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("authToken");
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(url, {
    ...options,
    credentials: "include",
    headers,
  });
};

// Helper to build full URL for fetch calls
export const buildApiUrl = (path) => {
  return `${API_BASE_URL}${path}`;
};

export default api;
